// Local record of the client-form links that have been emailed to a customer.
//
// `get-submitted-treatment-forms` only reports the forms that came back filled
// in, and `crm/send-form-link` doesn't return a send history — so the panel had
// no way to show the other half of the story: what was sent and is still
// waiting on the client. Every successful send is logged here so the Client
// Forms > Submitted tab can list it under "Sent".
//
// This lives in localStorage, so it is per-browser: a send made on another
// machine won't show up until the API exposes its own list. When it does,
// pass that list to `getSentForms` as `apiSent` — API rows win over local ones.

const STORAGE_KEY = 'clientFormSentLog';
const MAX_ENTRIES = 500;                       // keep the log from growing unbounded

// Buckets returned by `get-treatment-forms`, with the tag the panel shows.
export const FORM_BUCKETS = [
    { key: 'consent', tag: 'Consent' },
    { key: 'customer_service_form', tag: 'Service' },
    { key: 'chart_form', tag: 'Chart' },
];

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const readAll = () => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
        return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
        return [];
    }
};

const writeAll = (entries) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
    } catch (err) {
        // Storage disabled or full — sending still worked, we just can't remember it.
    }
};

/**
 * Look up a form (and its tag) in a `get-treatment-forms` payload by id, so the
 * send handlers can log the form's name without every call site passing it in.
 */
export const resolveFormMeta = (allForms, formId) => {
    for (const bucket of FORM_BUCKETS) {
        const match = allForms?.[bucket.key]?.find(
            (form) => sameId(form?.treatment_form_id, formId) || sameId(form?.id, formId),
        );
        if (match) return { ...match, tag: bucket.tag };
    }
    return null;
};

/**
 * Log a sent form link. One row per form per customer — re-sending refreshes the
 * timestamp instead of stacking duplicates.
 */
export const recordSentForm = (entry) => {
    if (!entry?.customer_id || !entry?.treatment_form_id) return readAll();
    const others = readAll().filter(
        (row) => !(sameId(row?.customer_id, entry.customer_id)
            && sameId(row?.treatment_form_id, entry.treatment_form_id)),
    );
    const next = [{ ...entry, sent_at: new Date().toISOString() }, ...others];
    writeAll(next);
    return next;
};

const normalizeApiRow = (row) => ({
    ...row,
    sent_at: row?.sent_at || row?.sent_time || row?.created_at || null,
    from_api: true,
});

/**
 * Forms emailed to `customerId`, newest first. `apiSent` is the optional list
 * from the backend; rows it already covers are not duplicated from the log.
 */
export const getSentForms = (customerId, apiSent) => {
    const fromApi = (Array.isArray(apiSent) ? apiSent : []).map(normalizeApiRow);
    const local = readAll().filter((row) => sameId(row?.customer_id, customerId));
    const merged = [
        ...fromApi,
        ...local.filter((row) => !fromApi.some((api) => sameId(api?.treatment_form_id, row?.treatment_form_id))),
    ];
    return merged.sort((a, b) => new Date(b?.sent_at || 0) - new Date(a?.sent_at || 0));
};
